import { useEffect, useRef } from 'react'

export type UseOnTabRefocusOptions = {
  minHiddenMs?: number
  /** Also treat window blur/focus as leaving/returning, for side-by-side windows. */
  includeWindowFocus?: boolean
}

export const DEFAULT_MIN_HIDDEN_MS = 5_000

export function useOnTabRefocus(
  callback: () => void,
  {
    minHiddenMs = DEFAULT_MIN_HIDDEN_MS,
    includeWindowFocus = false,
  }: UseOnTabRefocusOptions = {},
): void {
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    let awayAt: number | null = null

    const leave = () => {
      if (awayAt == null) awayAt = Date.now()
    }

    const back = () => {
      const since = awayAt
      awayAt = null
      if (since == null || Date.now() - since < minHiddenMs) return
      callbackRef.current()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') leave()
      else back()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    if (includeWindowFocus) {
      window.addEventListener('blur', leave)
      window.addEventListener('focus', back)
    }
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (includeWindowFocus) {
        window.removeEventListener('blur', leave)
        window.removeEventListener('focus', back)
      }
    }
  }, [minHiddenMs, includeWindowFocus])
}
